import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { UserProfile } from '../contexts/AuthContext';
import { useGlossary } from '../contexts/GlossaryContext';
import { createSangatiGroup, saveSangatiGroup } from '../lib/sangati';
import '../styles/TutorOnDemand.css';

const SESSION_MODES = ['online', 'offline'] as const;

const AVAILABILITY_SLOTS = ['morning', 'afternoon', 'evening', 'weekends'] as const;

interface TutorOnDemandProps {
  onNavigate?: (path: string) => void;
}

export default function TutorOnDemand({ onNavigate }: TutorOnDemandProps) {
  const { t } = useGlossary();
  const {
    user,
    profile,
    authLoading,
    signInWithGoogle,
    signInWithX,
    updateProfile,
    syncDiscovery,
    removeFromDiscovery,
  } = useAuth();

  const [draft, setDraft] = useState<UserProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const sessionLabel = (value: (typeof SESSION_MODES)[number]) => t(`tutor.session.${value}`);
  const availabilityLabel = (value: (typeof AVAILABILITY_SLOTS)[number]) => t(`tutor.availability.${value}`);
  const introPanel = () => (
    <section className="tutor-intro-panel" aria-label={t('tutor.description')}>
      <div className="tutor-intro-kicker">{t('tutor.eyebrow')}</div>
      <h2>{t('tutor.intro.title')}</h2>
      {t('tutor.description')
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .map((paragraph, index) => (
          <p key={index} className="tutor-intro-paragraph">
            {paragraph}
          </p>
        ))}
    </section>
  );

  useEffect(() => {
    if (profile) {
      setDraft({
        ...profile,
        profileKind: 'knowledge_provider',
        lookingFor: 'student',
      });
    } else if (!user) {
      setDraft(null);
    }
  }, [profile, user]);

  const handleSignIn = async (provider: 'google' | 'x') => {
    setError('');
    try {
      await (provider === 'google' ? signInWithGoogle() : signInWithX());
    } catch (err) {
      console.error(`${provider} sign-in failed:`, err);
        setError(provider === 'google'
          ? t('tutor.error.googleSignIn')
          : t('tutor.error.xSignIn'));
    }
  };

  const setField = <K extends keyof UserProfile>(key: K, value: UserProfile[K]) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: value });
  };

  const toggleAvailability = (slot: UserProfile['availability'][number]) => {
    if (!draft) return;
    const current = draft.availability ?? [];
    const next = current.includes(slot)
      ? current.filter((item) => item !== slot)
      : [...current, slot];
    setDraft({ ...draft, availability: next });
  };

  const toggleSessionMode = (mode: 'online' | 'offline') => {
    if (!draft) return;
    const current = draft.sessionModes ?? [];
    const next = current.includes(mode)
      ? current.filter((item) => item !== mode)
      : [...current, mode];
    setDraft({ ...draft, sessionModes: next });
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;

    setSaving(true);
    setError('');
    try {
      const nextProfile: UserProfile = {
        ...draft,
        profileKind: 'knowledge_provider',
        lookingFor: 'student',
        updatedAt: new Date().toISOString(),
      } as UserProfile;
      updateProfile(nextProfile);
      if (nextProfile.isDiscoverable) await syncDiscovery(nextProfile);
      else await removeFromDiscovery(nextProfile);
      setNotice(t('tutor.notice.saved'));
      setTimeout(() => setNotice(''), 3500);
    } catch (err) {
      console.error('Provider onboarding save failed:', err);
      setError(t('tutor.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const publicSummary = draft ? [
    `${t('tutor.summary.label.alias')}: ${draft.alias || t('tutor.summary.empty.alias')}`,
    `${t('tutor.summary.label.age')}: ${draft.age || t('tutor.summary.empty.age')}`,
    `${t('tutor.summary.label.pincode')}: ${draft.pincode || t('tutor.summary.empty.pincode')}`,
    `${t('tutor.summary.label.subjects')}: ${draft.studyAreas || t('tutor.summary.empty.subjects')}`,
    `${t('tutor.summary.label.qualification')}: ${draft.qualification || t('tutor.summary.empty.qualification')}`,
    `${t('tutor.summary.label.rating')}: ${draft.rating || t('tutor.summary.empty.rating')}`,
  ] : [];

  if (authLoading) {
    return (
      <div className="tutor-on-demand">
        <header className="tutor-header">
          <div className="tutor-header-copy">
            <div className="tutor-eyebrow">{t('tutor.eyebrow')}</div>
            <h1>{t('tutor.title')}</h1>
            <p className="subtitle">{t('tutor.loading')}</p>
          </div>
        </header>
        {introPanel()}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="tutor-on-demand">
        <header className="tutor-header">
          <div className="tutor-header-copy">
            <div className="tutor-eyebrow">{t('tutor.eyebrow')}</div>
            <h1>{t('tutor.title')}</h1>
            <p className="subtitle">{t('tutor.subtitle')}</p>
          </div>
        </header>

        {introPanel()}

        {error && <div className="tutor-error">{error}</div>}

        <section className="tutor-auth-panel">
          <h2>{t('tutor.auth.title')}</h2>
          <p>
            {t('tutor.auth.body')}
          </p>
          <div className="tutor-auth-actions">
            <button className="tutor-auth-btn tutor-auth-btn--google" onClick={() => handleSignIn('google')}>
              {t('tutor.auth.google')}
            </button>
            <button className="tutor-auth-btn tutor-auth-btn--x" onClick={() => handleSignIn('x')}>
              {t('tutor.auth.x')}
            </button>
          </div>
          <div className="tutor-privacy-card">
            <strong>{t('tutor.privacy.title')}</strong> {t('tutor.privacy.body')}
          </div>
        </section>
      </div>
    );
  }

  if (!profile || !draft) {
    return (
      <div className="tutor-on-demand">
        <header className="tutor-header">
          <div className="tutor-header-copy">
            <div className="tutor-eyebrow">{t('tutor.eyebrow')}</div>
            <h1>{t('tutor.title')}</h1>
            <p className="subtitle">{t('tutor.loadingProfile')}</p>
          </div>
        </header>
        {introPanel()}
      </div>
    );
  }

  return (
    <div className="tutor-on-demand">
      <header className="tutor-header">
        <div className="tutor-header-copy">
          <div className="tutor-eyebrow">{t('tutor.eyebrow')}</div>
          <h1>{t('tutor.title')}</h1>
          <p className="subtitle">{t('tutor.subtitleShort')}</p>
        </div>
      </header>

      {introPanel()}

      {notice && <div className="tutor-success">{notice}</div>}
      {error && <div className="tutor-error">{error}</div>}

      <div className="tutor-content">
        <section className="tutor-form-section">
          <div className="tutor-section-head">
            <h2>{t('tutor.section.details')}</h2>
            <p>{t('tutor.section.detailsBody')}</p>
          </div>

          <form className="tutor-form" onSubmit={handleSave}>
            <div className="tutor-grid">
              <label className="tutor-field">
                <span>{t('tutor.field.alias')}</span>
                <input
                  value={draft.alias}
                  onChange={(e) => setField('alias', e.target.value)}
                  placeholder={t('tutor.placeholder.alias')}
                  maxLength={40}
                />
              </label>

              <label className="tutor-field">
                <span>{t('tutor.field.age')}</span>
                <input
                  value={draft.age}
                  onChange={(e) => setField('age', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                  placeholder={t('tutor.placeholder.age')}
                  inputMode="numeric"
                  maxLength={3}
                />
              </label>

              <label className="tutor-field">
                <span>{t('tutor.field.pincode')}</span>
                <input
                  value={draft.pincode}
                  onChange={(e) => setField('pincode', e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder={t('tutor.placeholder.pincode')}
                  inputMode="numeric"
                  maxLength={6}
                />
              </label>

              <label className="tutor-field tutor-field--full">
                <span>{t('tutor.field.studyAreas')}</span>
                <textarea
                  value={draft.studyAreas}
                  onChange={(e) => setField('studyAreas', e.target.value)}
                  placeholder={t('tutor.placeholder.studyAreas')}
                  rows={3}
                />
              </label>

              <label className="tutor-field tutor-field--full">
                <span>{t('tutor.field.qualification')}</span>
                <textarea
                  value={draft.qualification}
                  onChange={(e) => setField('qualification', e.target.value)}
                  placeholder={t('tutor.placeholder.qualification')}
                  rows={3}
                />
              </label>

              <label className="tutor-field">
                <span>{t('tutor.field.rating')}</span>
                <input
                  value={draft.rating}
                  onChange={(e) => setField('rating', e.target.value.replace(/[^0-9.]/g, '').slice(0, 3))}
                  placeholder="4.5"
                  inputMode="decimal"
                />
              </label>

              <div className="tutor-field tutor-field--full">
                <span>{t('tutor.field.sessionModes')}</span>
                <div className="tutor-chip-row">
                  {SESSION_MODES.map((mode) => {
                    const checked = draft.sessionModes.includes(mode);
                    return (
                      <button
                        key={mode}
                        type="button"
                        className={`tutor-chip ${checked ? 'tutor-chip--on' : ''}`}
                        onClick={() => toggleSessionMode(mode)}
                      >
                        {sessionLabel(mode)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="tutor-field tutor-field--full">
                <span>{t('tutor.field.availability')}</span>
                <div className="tutor-chip-row">
                  {AVAILABILITY_SLOTS.map((slot) => {
                    const checked = draft.availability.includes(slot);
                    return (
                      <button
                        key={slot}
                        type="button"
                        className={`tutor-chip ${checked ? 'tutor-chip--on' : ''}`}
                        onClick={() => toggleAvailability(slot)}
                      >
                        {availabilityLabel(slot)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="tutor-actions">
              <label className={`tutor-discovery-toggle ${draft.isDiscoverable ? 'tutor-discovery-toggle--on' : ''}`}>
                <input
                  type="checkbox"
                  checked={draft.isDiscoverable}
                  onChange={(e) => setField('isDiscoverable', e.target.checked)}
                />
                {t('tutor.toggle.discovery')}
              </label>

              <button className="tutor-submit-button" type="submit" disabled={saving}>
                {saving ? t('tutor.button.saving') : t('tutor.button.save')}
              </button>
              <button
                className="tutor-submit-button tutor-submit-button--secondary"
                type="button"
                onClick={() => {
                  if (!draft) return;
                  const now = new Date().toISOString();
                  const current = { ...draft, profileKind: 'knowledge_provider', lookingFor: 'student', updatedAt: now } as UserProfile;
                  updateProfile(current);
                  const group = createSangatiGroup(current);
                  saveSangatiGroup(group);
                  onNavigate?.('/sangati');
                }}
                disabled={saving}
              >
                {t('tutor.button.createGroup')}
              </button>
            </div>
          </form>
        </section>

        <aside className="tutor-summary">
          <h3>{t('tutor.summary.title')}</h3>
          <p className="tutor-summary-note">
            {t('tutor.summary.note')}
          </p>
          <ul>
            {publicSummary.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <div className="tutor-summary-box">
            <strong>{t('tutor.summary.timeZone')}</strong>
            <div>{profile.timeZone || t('tutor.summary.fillNow')}</div>
          </div>
          <div className="tutor-summary-box">
            <strong>{t('tutor.summary.languageScript')}</strong>
              <div>{profile.nativeLanguage || t('tutor.summary.fillNow')}</div>
            <div>{t(`profile.script.${profile.preferredScript}`)}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
